/**
 * Creating CouponDetails component for Coupons
 *
 * @module views/components/CouponDetails
 * @memberof -Common
 */
import './CouponDetails.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Image from '@ulta/core/components/Image/Image';
import Markdown from '@ulta/core/components/Markdown/Markdown';
import Text from '@ulta/core/components/Text/Text';

import CopyToClipboard from '@ulta/components/CopyToClipboard/CopyToClipboard';
import Divider from '@ulta/components/Divider/Divider';

import { printPage } from '@ulta/utils/printUtils/printUtils';

/**
  * Represents a CouponDetails component
  *
  * @method
  * @param {CouponDetailsProps} props - React properties passed from composition
  * @returns CouponDetails
  */
export const CouponDetails = function( props ){
  const {
    id,
    sectionTitle,
    couponTitle,
    couponDetails,
    barCode,
    disclaimer,
    printIcon,
    couponCode,
    couponCodeLabel,
    copyToClipAction,
    copyClipAccessibilityLabel,
    copiedClipAccessibilityLabel,
    printAccessibilityLabel,
    copiedToClipLabel,
    copyIcon,
    printAction
  } = props;

  if( !sectionTitle && !barCode && !barCode?.imageUrl && !couponTitle && !couponDetails && !couponCode && !disclaimer ){
    return null;
  }
  return (
    <div id={ `${ id }` }
      className='CouponDetails'
    >
      <div className='CouponDetails__couponSection'>
        <div className='CouponDetails__header'>
          { sectionTitle &&
          <Text
            textStyle='title-6'
            color='black'
            htmlTag='h2'
          >
            { sectionTitle }
          </Text>
          }
          <span className='CouponDetails__header--printButton'>
            { printIcon && printAction?.label &&
              <Button
                ariaLabel={ printAccessibilityLabel }
                iconImage='Print'
                iconSize={ 's' }
                label={ printAction.label }
                onClick={ () => printPage( { divId: id } ) }
                tertiary
                compact
              /> }
          </span>
        </div>
        <div className='CouponDetails__couponInfo'>
          { barCode?.imageUrl && barCode.altText &&
            <Image
              alt={ barCode.altText }
              src={ barCode.imageUrl }
            /> }
          { couponTitle &&
            <div className='CouponDetails__couponTitle'>
              <Text
                textStyle='title-4'
                color='magenta-500'
                htmlTag='h3'
              >
                { couponTitle }
              </Text>
            </div>
          }
          { couponDetails &&
            <div className='CouponDetails__couponDetails'>
              <Markdown
                bodyStyle='body-2'
                cmsMarkdownContent={ couponDetails }
              />
            </div>
          }
          <div className='CouponDetails__copyToClipboard'>
            { couponCode && couponCodeLabel &&
              <CopyToClipboard
                couponCode={ couponCode }
                couponCodeLabel={ couponCodeLabel }
                copyIcon={ copyIcon }
                copyToClipAction={ copyToClipAction }
                copyClipAccessibilityLabel={ copyClipAccessibilityLabel }
                copiedClipAccessibilityLabel={ copiedClipAccessibilityLabel }
                copiedToClipLabel={ copiedToClipLabel }
              />
            }
          </div>
          <div className='CouponDetails__disclaimer'>
            <Divider type='gray'> </Divider>
            { disclaimer &&
              <Markdown
                className='CouponDetails__disclaimer--markdown'
                bodyStyle='body-3'
                cmsMarkdownContent={ disclaimer }
              />
            }
          </div>
        </div>
      </div>
    </div>
  );
};

/**
  * Property type definitions
  * @typedef CouponDetailsProps
  * @type {object}
  * @property {string} sectionTitle - sets the text for sectionTitle
  * @property { object.<{imageUrl: string, altText: string }> } barCode - Provides the imageUrl, altText
  * @property {string} couponTitle - sets the text for couponTitle
  * @property {string} couponDetails - sets the text for couponDetails
  * @property {string} disclaimer - sets the text for disclaimer
  * @property {string} printAccessibilityLabel - aria-label for the print button
  * @property {string} printIcon - name of print icon
  * @property {function} printAction - Sets the label for print button
  *  @property {string} copyToClipAction - Sets the action for copy to clipboard
  * @property {string} copiedToClipLabel - Sets the label of the copied
  * @property {string} copyClipAccessibilityLabel - Sets the label for copy clip
  * @property {string} copiedClipAccessibilityLabel - Sets the accessiblity label for copied
  * @property {string} couponCodeLabel - Sets the label for coupon code
  * @property {string} couponCode - Sets the code value for coupon code
  * @property {string} copyIcon - label for the copy button
  *
  */
export const propTypes = {
  barCode: PropTypes.shape( {
    imageUrl: PropTypes.string,
    altText: PropTypes.string
  } ),
  sectionTitle: PropTypes.string,
  couponTitle: PropTypes.string,
  couponDetails: PropTypes.string,
  disclaimer: PropTypes.string,
  printAccessibilityLabel: PropTypes.string,
  printIcon: PropTypes.string,
  printAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  copiedToClipLabel: PropTypes.string,
  copyToClipAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  copyClipAccessibilityLabel: PropTypes.string,
  copiedClipAccessibilityLabel: PropTypes.string,
  couponCodeLabel: PropTypes.string,
  couponCode: PropTypes.string,
  copyIcon: PropTypes.string
};

CouponDetails.propTypes = propTypes;
export default CouponDetails;